import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Edit,
  Trash2,
  Phone,
  Mail,
  MapPin,
  CreditCard,
  DollarSign,
  User,
  UserCheck,
  Loader,
  FileText,
  X
} from 'lucide-react';
import { toast } from 'react-toastify';
import { formatDateToISO } from '../utils/formatters';
import { useAuth } from '../contexts/AuthContext';
import {
  getCustomerById,
  deleteCustomer,
  generateCustomerStatement,
  getCustomerSpendingTrend
} from '../api/customers';
import { getWholesalerByCustomerId } from '../api/wholesalers';
import CustomerSpendingChart from '../components/CustomerSpendingChart';
import LoadingSpinner from '../components/common/LoadingSpinner';

const CustomerDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showStatementModal, setShowStatementModal] = useState(false);
  const [statementDates, setStatementDates] = useState({
    startDate: formatDateToISO(new Date(new Date().setDate(new Date().getDate() - 30))),
    endDate: formatDateToISO(new Date())
  });
  const [generatingStatement, setGeneratingStatement] = useState(false);
  const [spendingTrend, setSpendingTrend] = useState([]);
  const [wholesaler, setWholesaler] = useState(null);
  const [loadingWholesaler, setLoadingWholesaler] = useState(false);

  const fetchCustomer = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getCustomerById(id);
      setCustomer(data);
    } catch (error) {
      toast.error('Failed to fetch customer details');
      navigate('/customers');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const fetchWholesaler = useCallback(async () => {
    if (!customer || !customer.is_wholesaler) return;

    setLoadingWholesaler(true);
    try {
      const data = await getWholesalerByCustomerId(id);
      setWholesaler(data?.data || null);
    } catch (error) {
      // Customer is not a wholesaler, ignore
      setWholesaler(null);
    } finally {
      setLoadingWholesaler(false);
    }
  }, [id, customer]);

  const fetchSpendingTrend = useCallback(async () => {
    try {
      const data = await getCustomerSpendingTrend(id);
      setSpendingTrend(data);
    } catch (error) {}
  }, [id]);

  useEffect(() => {
    fetchCustomer();
    fetchSpendingTrend();
  }, [fetchCustomer, fetchSpendingTrend]);

  useEffect(() => {
    if (customer?.is_wholesaler) {
      fetchWholesaler();
    }
  }, [customer, fetchWholesaler]);

  const handleDelete = async () => {
    if (
      !window.confirm(
        'Are you sure you want to delete this customer? This action cannot be undone.'
      )
    ) {
      return;
    }

    try {
      await deleteCustomer(id);
      toast.success('Customer deleted successfully');
      navigate('/customers');
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to delete customer';
      toast.error(errorMessage);
    }
  };

  const handleGenerateStatement = async () => {
    setGeneratingStatement(true);
    try {
      await generateCustomerStatement(id, statementDates.startDate, statementDates.endDate);
      toast.success('Statement generated successfully');
      setShowStatementModal(false);
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to generate statement';
      toast.error(errorMessage);
    } finally {
      setGeneratingStatement(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const isOwnerBranch = customer?.created_by_branch_id === user?.current_branch_id;
  const canEdit =
    ['super_admin', 'admin', 'retail_admin'].includes(user?.role) ||
    (user?.role === 'branch_admin' && isOwnerBranch);

  const getTypeColor = (type) => {
    switch (type) {
      case 'wholesaler':
        return 'bg-purple-100 text-purple-800';
      case 'retail':
        return 'bg-blue-100 text-blue-800';
      case 'contractor':
        return 'bg-green-100 text-green-800';
      case 'corporate':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return <LoadingSpinner className="min-h-screen" />;
  }

  if (!customer) {
    return (
      <div className="min-h-screen bg-secondary-offWhite flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-700 mb-2">Customer Not Found</h2>
          <button
            onClick={() => navigate('/customers')}
            className="text-accent-gold hover:underline"
          >
            Back to Customers
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary-offWhite">
      <div className="max-w-6xl mx-auto p-4 md:p-6">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/customers')}
            className="flex items-center gap-2 text-gray-600 hover:text-primary-black mb-4 min-h-[44px]"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm md:text-base">Back to Customers</span>
          </button>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex-1">
              <h1 className="text-2xl md:text-3xl font-bold text-primary-black break-words">
                {customer.full_name}
              </h1>
              <p className="text-sm md:text-base text-gray-600 mt-1">{customer.customer_code}</p>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <button
                onClick={() => setShowStatementModal(true)}
                className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2.5 bg-accent-gold text-primary-black font-semibold rounded-lg hover:bg-accent-goldDark transition-colors min-h-[44px]"
              >
                <FileText className="w-5 h-5" />
                <span className="text-sm md:text-base">Generate Statement</span>
              </button>
              {canEdit && (
                <>
                  <button
                    onClick={() => navigate(`/customers/${id}/edit`)}
                    className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors min-h-[44px]"
                  >
                    <Edit className="w-5 h-5" />
                    <span className="text-sm md:text-base">Edit</span>
                  </button>
                  <button
                    onClick={handleDelete}
                    className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors min-h-[44px]"
                  >
                    <Trash2 className="w-5 h-5" />
                    <span className="text-sm md:text-base">Delete</span>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Information */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-bold text-primary-black mb-4 flex items-center gap-2">
                <User className="w-6 h-6" />
                Basic Information
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-500">Full Name</label>
                  <p className="font-semibold text-gray-900">{customer.full_name}</p>
                </div>

                <div>
                  <label className="text-sm text-gray-500">Customer Code</label>
                  <p className="font-semibold text-gray-900">{customer.customer_code}</p>
                </div>

                <div>
                  <label className="text-sm text-gray-500">Customer Type</label>
                  <span
                    className={`inline-block px-3 py-1 text-sm font-semibold rounded ${getTypeColor(customer.is_wholesaler ? 'wholesaler' : customer.customer_type)}`}
                  >
                    {customer.is_wholesaler ? 'wholesaler' : customer.customer_type}
                  </span>
                </div>

                <div>
                  <label className="text-sm text-gray-500">Status</label>
                  <span
                    className={`inline-block px-3 py-1 text-sm font-semibold rounded ${
                      customer.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {customer.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-bold text-primary-black mb-4">Contact Information</h2>

              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-gray-500" />
                  <div>
                    <label className="text-sm text-gray-500">Phone</label>
                    <p className="font-semibold text-gray-900">{customer.phone}</p>
                  </div>
                </div>

                {customer.email && (
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-gray-500" />
                    <div>
                      <label className="text-sm text-gray-500">Email</label>
                      <p className="font-semibold text-gray-900">{customer.email}</p>
                    </div>
                  </div>
                )}

                {customer.address && (
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-gray-500 mt-1" />
                    <div>
                      <label className="text-sm text-gray-500">Address</label>
                      <p className="font-semibold text-gray-900">
                        {customer.address}
                        {customer.city && `, ${customer.city}`}
                        {customer.state && `, ${customer.state}`}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Referrer Information */}
            {customer.referrer && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-bold text-primary-black mb-4 flex items-center gap-2">
                  <UserCheck className="w-6 h-6" />
                  Referrer Information
                </h2>

                <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                  <div className="w-12 h-12 bg-accent-gold rounded-full flex items-center justify-center text-primary-black font-bold text-lg">
                    {customer.referrer.full_name.charAt(0)}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{customer.referrer.full_name}</p>
                    <p className="text-sm text-gray-500">{customer.referrer.referrer_code}</p>
                    <p className="text-sm text-gray-600">{customer.referrer.phone}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Wholesaler Information */}
            {customer.is_wholesaler && wholesaler && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-bold text-primary-black mb-4 flex items-center gap-2">
                  <CreditCard className="w-6 h-6" />
                  Wholesaler Credit Facility
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 bg-purple-50 rounded-lg border-2 border-purple-500">
                    <label className="text-sm text-purple-700">Credit Limit</label>
                    <p className="text-2xl font-bold text-purple-900">
                      {formatCurrency(wholesaler.credit_limit)}
                    </p>
                  </div>

                  <div
                    className={`p-4 rounded-lg border-2 ${
                      wholesaler.credit_balance > 0
                        ? 'bg-red-50 border-red-500'
                        : 'bg-green-50 border-green-500'
                    }`}
                  >
                    <label
                      className={`text-sm ${
                        wholesaler.credit_balance > 0 ? 'text-red-700' : 'text-green-700'
                      }`}
                    >
                      Outstanding Balance
                    </label>
                    <p
                      className={`text-2xl font-bold ${
                        wholesaler.credit_balance > 0 ? 'text-red-900' : 'text-green-900'
                      }`}
                    >
                      {formatCurrency(wholesaler.credit_balance)}
                    </p>
                  </div>

                  <div className="p-4 bg-blue-50 rounded-lg border-2 border-blue-500">
                    <label className="text-sm text-blue-700">Available Credit</label>
                    <p className="text-2xl font-bold text-blue-900">
                      {formatCurrency(wholesaler.credit_limit - wholesaler.credit_balance)}
                    </p>
                  </div>

                  <div className="p-4 bg-gray-50 rounded-lg border-2 border-gray-500">
                    <label className="text-sm text-gray-700">Payment Terms</label>
                    <p className="text-2xl font-bold text-gray-900">
                      {wholesaler.payment_terms_days} days
                    </p>
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm text-gray-500">Account Status</label>
                    <span
                      className={`inline-block px-3 py-1 text-sm font-semibold rounded mt-1 ${
                        wholesaler.status === 'active'
                          ? 'bg-green-100 text-green-800'
                          : wholesaler.status === 'suspended'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {wholesaler.status}
                    </span>
                  </div>

                  <div>
                    <label className="text-sm text-gray-500">Total Credit Purchases</label>
                    <p className="font-semibold text-gray-900 mt-1">
                      {formatCurrency(wholesaler.total_credit_purchases || 0)}
                    </p>
                  </div>

                  <div>
                    <label className="text-sm text-gray-500">Total Payments</label>
                    <p className="font-semibold text-gray-900 mt-1">
                      {formatCurrency(wholesaler.total_payments_received || 0)}
                    </p>
                  </div>
                </div>

                {wholesaler.notes && (
                  <div className="mt-4">
                    <label className="text-sm text-gray-500">Notes</label>
                    <p className="text-gray-700 mt-1 whitespace-pre-wrap">{wholesaler.notes}</p>
                  </div>
                )}
              </div>
            )}

            {customer.is_wholesaler && loadingWholesaler && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center justify-center">
                  <Loader className="w-8 h-8 text-accent-gold animate-spin" />
                </div>
              </div>
            )}
            {/* Spending Trend Chart */}
            <CustomerSpendingChart data={spendingTrend} chartType="line" />
          </div>

          {/* Financial Summary */}
          <div className="space-y-6">
            {/* Financial Stats */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-bold text-primary-black mb-4 flex items-center gap-2">
                <DollarSign className="w-6 h-6" />
                Financial Summary
              </h2>

              <div className="space-y-4">
                <div className="p-4 bg-blue-50 rounded-lg border-2 border-blue-500">
                  <label className="text-sm text-blue-700">Total Purchases</label>
                  <p className="text-2xl font-bold text-blue-900">
                    {formatCurrency(customer.total_purchases)}
                  </p>
                </div>

                <div
                  className={`p-4 rounded-lg border-2 ${
                    customer.current_balance < 0
                      ? 'bg-red-50 border-red-500'
                      : 'bg-green-50 border-green-500'
                  }`}
                >
                  <label
                    className={`text-sm ${
                      customer.current_balance < 0 ? 'text-red-700' : 'text-green-700'
                    }`}
                  >
                    Current Balance
                  </label>
                  <p
                    className={`text-2xl font-bold ${
                      customer.current_balance < 0 ? 'text-red-900' : 'text-green-900'
                    }`}
                  >
                    {formatCurrency(customer.current_balance)}
                  </p>
                </div>
              </div>
            </div>

            {/* Meta Information */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-bold text-primary-black mb-4">Additional Info</h2>

              <div className="space-y-3">
                <div>
                  <label className="text-sm text-gray-500">Created Date</label>
                  <p className="font-semibold text-gray-900">{formatDate(customer.created_at)}</p>
                </div>

                {customer.updated_at && (
                  <div>
                    <label className="text-sm text-gray-500">Last Updated</label>
                    <p className="font-semibold text-gray-900">{formatDate(customer.updated_at)}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Statement Generation Modal */}
        {showStatementModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <h2 className="text-lg sm:text-xl font-bold text-primary-black">
                  Generate Statement
                </h2>
                <button
                  onClick={() => setShowStatementModal(false)}
                  className="text-gray-400 hover:text-gray-600 min-h-[44px] min-w-[44px] flex items-center justify-center"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                  <input
                    type="date"
                    value={statementDates.startDate}
                    onChange={(e) =>
                      setStatementDates({ ...statementDates, startDate: e.target.value })
                    }
                    max={statementDates.endDate}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent min-h-[44px]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                  <input
                    type="date"
                    value={statementDates.endDate}
                    onChange={(e) =>
                      setStatementDates({ ...statementDates, endDate: e.target.value })
                    }
                    min={statementDates.startDate}
                    max={formatDateToISO(new Date())}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent min-h-[44px]"
                  />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 mt-6">
                <button
                  onClick={() => setShowStatementModal(false)}
                  disabled={generatingStatement}
                  className="w-full sm:flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 min-h-[44px]"
                >
                  Cancel
                </button>
                <button
                  onClick={handleGenerateStatement}
                  disabled={generatingStatement}
                  className="w-full sm:flex-1 px-4 py-2.5 bg-accent-gold text-primary-black font-semibold rounded-lg hover:bg-accent-goldDark transition-colors disabled:opacity-50 flex items-center justify-center gap-2 min-h-[44px]"
                >
                  {generatingStatement ? (
                    <>
                      <Loader className="w-5 h-5 animate-spin" />
                      <span className="text-sm sm:text-base">Generating...</span>
                    </>
                  ) : (
                    <>
                      <FileText className="w-5 h-5" />
                      <span className="text-sm sm:text-base">Generate PDF</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerDetailPage;
